/**
 * MUTAGIM.SHOP — Admin Product Edit Page
 * Page d'édition de produit avec régénération IA et assignation de catégories
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminPageWrapper } from '../components/admin/AdminPageWrapper';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Save,
  Loader2,
  Sparkles,
  ArrowLeft,
  Eye,
  Package,
  Tag,
  FolderOpen,
  Image as ImageIcon,
  Check,
  X,
  RefreshCw,
  ArrowRight,
  ChevronRight,
  Mic,
  Wand2,
  Star
} from 'lucide-react';
import { VoiceInput } from '../components/ui/VoiceInput';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminProductEditPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { isLight } = useAdminTheme();
  
  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: { fr: '', en: '', he: '' },
    description: { fr: '', en: '', he: '' },
    price: 0,
    compare_at_price: null,
    margin_coefficient: 2.5, // Coefficient multiplicateur par défaut
    status: 'published',
    category_ids: [],
    tags: [],
    is_featured: false
  });
  
  // Prix source du produit
  const [sourcePrice, setSourcePrice] = useState(null);
  
  const [newTag, setNewTag] = useState('');
  
  // Image gallery state
  const [selectedFeaturedImage, setSelectedFeaturedImage] = useState(null);
  
  // AI Preview state
  const [aiPreviewOpen, setAiPreviewOpen] = useState(false);
  const [aiPreviewData, setAiPreviewData] = useState(null);
  const [previewTab, setPreviewTab] = useState('fr');
  
  const [aiImageDialogOpen, setAiImageDialogOpen] = useState(false);
  const [aiImagePrompt, setAiImagePrompt] = useState('');
  const [generatingAiImage, setGeneratingAiImage] = useState(false);

  const token = localStorage.getItem('admin_token');

  // Fetch product
  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/products/admin/${productId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Product not found');
      
      const data = await res.json();
      setProduct(data);
      
      // Récupérer le prix source
      setSourcePrice(data.source_price || null);
      
      // Calculer le coefficient si on a le prix source et le prix de vente
      let coefficient = data.margin_coefficient || 2.5;
      if (data.source_price?.price_usd && data.price?.amount) {
        // Convertir le prix ILS en USD pour le calcul (taux approximatif)
        const priceUSD = data.price.amount / 3.7;
        coefficient = Math.round((priceUSD / data.source_price.price_usd) * 10) / 10;
      }
      
      setFormData({
        name: data.name || { fr: '', en: '', he: '' },
        description: data.description || { fr: '', en: '', he: '' },
        price: data.price?.amount || data.price || 0,
        compare_at_price: data.price?.compare_at || data.compare_at_price || null,
        margin_coefficient: coefficient,
        status: data.status || 'published',
        category_ids: data.category_ids || (data.category_id ? [data.category_id] : []),
        tags: data.tags || [],
        is_featured: data.is_featured || false
      });
      
      // Charger l'image mise en avant si elle existe
      if (data.images?.featured_image) {
        setSelectedFeaturedImage(data.images.featured_image);
      } else if (data.images?.main?.[0]) {
        setSelectedFeaturedImage(data.images.main[0]);
      }
    } catch (err) {
      console.error('Error fetching product:', err);
      toast.error('Produit non trouvé');
      navigate('/admin/sourcing');
    } finally {
      setLoading(false);
    }
  }, [productId, token, navigate]);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/categories/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setCategories(data.categories || []);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchProduct();
    fetchCategories();
  }, [fetchProduct, fetchCategories]);

  // Handle AI regeneration
  const handleRegenerateAI = async () => {
    try {
      setRegenerating(true);
      toast.info('Régénération du contenu IA en cours...', { duration: 15000, id: 'ai-regen' });
      
      const res = await fetch(`${API_URL}/api/products/admin/${productId}/regenerate-ai`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to regenerate content');
      }
      
      const result = await res.json();
      
      toast.dismiss('ai-regen');
      
      // Store the new content for preview instead of applying directly
      if (result.product) {
        setAiPreviewData({
          old: {
            name: { ...formData.name },
            description: { ...formData.description },
            tags: [...formData.tags]
          },
          new: {
            name: result.product.name || formData.name,
            description: result.product.description || formData.description,
            tags: result.product.tags || formData.tags
          }
        });
        setAiPreviewOpen(true);
        toast.success('Nouveau contenu généré ! Vérifiez les changements.');
      }
    } catch (err) {
      console.error('Error regenerating AI content:', err);
      toast.dismiss('ai-regen');
      toast.error(err.message || 'Erreur lors de la régénération');
    } finally {
      setRegenerating(false);
    }
  };

  // Apply AI preview changes
  const applyAIChanges = () => {
    if (aiPreviewData?.new) {
      setFormData(prev => ({
        ...prev,
        name: aiPreviewData.new.name,
        description: aiPreviewData.new.description,
        tags: aiPreviewData.new.tags
      }));
      setAiPreviewOpen(false);
      setAiPreviewData(null);
      toast.success('Modifications appliquées ! N\'oubliez pas d\'enregistrer.');
    }
  };

  // Reject AI changes
  const rejectAIChanges = () => {
    setAiPreviewOpen(false);
    setAiPreviewData(null);
    toast.info('Modifications annulées');
  };

  // Generate AI image for product
  const handleGenerateAiImage = async () => {
    if (!aiImagePrompt.trim()) {
      toast.error('Veuillez entrer une description pour l\'image');
      return;
    }

    try {
      setGeneratingAiImage(true);
      toast.info('Génération de l\'image en cours...', { duration: 30000, id: 'gen-ai-img' });

      const fullPrompt = `Professional e-commerce product photo: ${aiImagePrompt}. 
Product: ${formData.name?.fr || product?.name?.fr || 'fashion item'}.
Style: Clean white or neutral background, professional studio lighting, high-quality product photography.
Requirements: NO text, NO watermarks, photorealistic, commercial quality.`;

      const res = await fetch(`${API_URL}/api/ai/generate-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prompt: fullPrompt,
          size: "1024x1024"
        })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Échec de la génération');
      }

      const result = await res.json();
      toast.dismiss('gen-ai-img');

      if (result.success && result.image_url) {
        // Add the new image to the product's images
        const newImageUrl = result.image_url.startsWith('/api') 
          ? result.image_url 
          : `/api${result.image_url}`;
        
        // Update the product with the new AI image
        const updateRes = await fetch(`${API_URL}/api/products/admin/${productId}/add-ai-image`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ image_url: newImageUrl })
        });

        if (updateRes.ok) {
          toast.success('Image générée et ajoutée à la galerie !');
          setAiImageDialogOpen(false);
          setAiImagePrompt('');
          // Refresh product data
          window.location.reload();
        } else {
          toast.success('Image générée ! Rechargez la page pour la voir.');
        }
      }
    } catch (err) {
      toast.dismiss('gen-ai-img');
      toast.error(err.message || 'Erreur lors de la génération');
    } finally {
      setGeneratingAiImage(false);
    }
  };

  // Handle save
  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Préparer les données avec le prix structuré
      const dataToSave = {
        ...formData,
        price: {
          amount: formData.price,
          currency: 'ILS'
        },
        margin_coefficient: formData.margin_coefficient
      };
      
      const res = await fetch(`${API_URL}/api/products/admin/${productId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSave)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save product');
      }
      
      toast.success('Produit mis à jour');
    } catch (err) {
      console.error('Error saving product:', err);
      toast.error(err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Handle category toggle
  const toggleCategory = (categoryId) => {
    setFormData(prev => {
      const current = prev.category_ids || [];
      if (current.includes(categoryId)) {
        return { ...prev, category_ids: current.filter(id => id !== categoryId) };
      } else {
        return { ...prev, category_ids: [...current, categoryId] };
      }
    });
  };

  // Handle tag add
  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim().toLowerCase())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim().toLowerCase()]
      }));
      setNewTag('');
    }
  };

  // Handle tag remove
  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  if (loading) {
    return (
      <AdminPageWrapper title="Chargement...">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        </div>
      </AdminPageWrapper>
    );
  }

  const mainImage = product?.images?.main?.[0] || product?.images?.variants?.[0]?.url || '/placeholder.png';
  
  // Get all images for gallery
  const getAllImages = () => {
    const images = [];
    const seenUrls = new Set();
    
    const addImage = (url, source = 'unknown', label = null) => {
      if (!url || seenUrls.has(url)) return;
      seenUrls.add(url);
      images.push({ url, source, label });
    };
    
    if (product?.images) {
      // AI generated images first (highest quality)
      if (product.images.ai_generated && Array.isArray(product.images.ai_generated)) {
        product.images.ai_generated.forEach(img => addImage(img, 'ai'));
      }
      // Main images
      if (product.images.main && Array.isArray(product.images.main)) {
        product.images.main.forEach(img => addImage(img, 'main'));
      }
      // Variant images - include label
      if (product.images.variants && Array.isArray(product.images.variants)) {
        product.images.variants.forEach(v => {
          if (v.path) addImage(v.path, 'variant', v.label);
        });
      }
      // QC photos
      if (product.images.qc && Array.isArray(product.images.qc)) {
        product.images.qc.forEach(img => addImage(img, 'qc'));
      }
    }
    
    // Legacy ai_generated_images field
    if (product?.ai_generated_images && Array.isArray(product.ai_generated_images)) {
      product.ai_generated_images.forEach(img => img.url && addImage(img.url, 'ai'));
    }
    
    return images;
  };
  
  const allImages = getAllImages();
  const featuredImage = selectedFeaturedImage || (allImages[0]?.url) || mainImage;
  
  // Handle setting featured image
  const handleSetFeaturedImage = async (imageUrl) => {
    setSelectedFeaturedImage(imageUrl);
    
    // Update in backend
    try {
      const res = await fetch(`${API_URL}/api/products/admin/${productId}/featured-image`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ featured_image: imageUrl })
      });
      
      if (res.ok) {
        toast.success('Image mise en avant mise à jour');
      }
    } catch (err) {
      console.error('Error updating featured image:', err);
    }
  };
  
  // Get proper image URL
  const getImgUrl = (img) => {
    if (!img) return '/placeholder.png';
    if (img.startsWith('/api')) return `${API_URL}${img}`;
    if (img.startsWith('http')) return img;
    return img;
  };

  return (
    <AdminPageWrapper 
      title="Modifier le produit" 
      subtitle={formData.name?.fr || product?.slug}
    >
      <div className="space-y-6" data-testid="admin-product-edit-page">
        {/* Header Actions */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/sourcing')}
            className={isLight ? "border-gray-300" : "border-zinc-700"}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux produits
          </Button>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => window.open(`/product/${product?.slug}`, '_blank')}
              className={isLight ? "border-gray-300" : "border-zinc-700"}
            >
              <Eye className="w-4 h-4 mr-2" />
              Voir sur le site
            </Button>
            
            <Button
              onClick={handleRegenerateAI}
              disabled={regenerating}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
              data-testid="regenerate-ai-btn"
            >
              {regenerating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Régénérer contenu IA
            </Button>
            
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-product-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Enregistrer
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Product Info Card */}
            <Card className={isLight ? "bg-white border-gray-200" : "bg-zinc-900/50 border-zinc-800"}>
              <CardHeader>
                <CardTitle className={cn("flex items-center gap-2", isLight ? "text-gray-900" : "text-white")}>
                  <Package className="w-5 h-5" />
                  Informations produit
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="fr" className="w-full">
                  <TabsList className={cn(
                    "grid w-full grid-cols-3 mb-4",
                    isLight ? "bg-gray-100" : "bg-zinc-800"
                  )}>
                    <TabsTrigger value="fr">Français</TabsTrigger>
                    <TabsTrigger value="en">English</TabsTrigger>
                    <TabsTrigger value="he">עברית</TabsTrigger>
                  </TabsList>
                  
                  {['fr', 'en', 'he'].map((lang) => (
                    <TabsContent key={lang} value={lang} className="space-y-4">
                      <div className="space-y-2">
                        <Label className={isLight ? "text-gray-700" : "text-gray-300"}>
                          Titre {lang.toUpperCase()}
                        </Label>
                        <Input
                          value={formData.name?.[lang] || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            name: { ...prev.name, [lang]: e.target.value }
                          }))}
                          placeholder={`Titre en ${lang === 'fr' ? 'français' : lang === 'en' ? 'anglais' : 'hébreu'}`}
                          dir={lang === 'he' ? 'rtl' : 'ltr'}
                          className={cn(
                            isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                          )}
                          data-testid={`product-name-${lang}`}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label className={isLight ? "text-gray-700" : "text-gray-300"}>
                          Description {lang.toUpperCase()}
                        </Label>
                        <Textarea
                          value={formData.description?.[lang] || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            description: { ...prev.description, [lang]: e.target.value }
                          }))}
                          placeholder={`Description en ${lang === 'fr' ? 'français' : lang === 'en' ? 'anglais' : 'hébreu'}`}
                          dir={lang === 'he' ? 'rtl' : 'ltr'}
                          rows={4}
                          className={cn(
                            isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                          )}
                          data-testid={`product-description-${lang}`}
                        />
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </CardContent>
            </Card>

            {/* Tags Card */}
            <Card className={isLight ? "bg-white border-gray-200" : "bg-zinc-900/50 border-zinc-800"}>
              <CardHeader>
                <CardTitle className={cn("flex items-center gap-2", isLight ? "text-gray-900" : "text-white")}>
                  <Tag className="w-5 h-5" />
                  Tags
                </CardTitle>
                <CardDescription className={isLight ? "text-gray-600" : "text-gray-400"}>
                  Tags pour le SEO et la recherche
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Ajouter un tag..."
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    className={cn(
                      "flex-1",
                      isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                    )}
                  />
                  <Button onClick={addTag} variant="outline" className={isLight ? "border-gray-300" : "border-zinc-700"}>
                    Ajouter
                  </Button>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className={cn(
                        "px-3 py-1 cursor-pointer hover:bg-red-500/20 transition-colors",
                        isLight ? "bg-gray-100 text-gray-700" : "bg-zinc-800 text-gray-300"
                      )}
                      onClick={() => removeTag(tag)}
                    >
                      {tag}
                      <X className="w-3 h-3 ml-2" />
                    </Badge>
                  ))}
                  {formData.tags.length === 0 && (
                    <p className={cn("text-sm", isLight ? "text-gray-500" : "text-gray-500")}>
                      Aucun tag. Utilisez la régénération IA pour en créer automatiquement.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Product Images Gallery */}
            <Card className={isLight ? "bg-white border-gray-200" : "bg-zinc-900/50 border-zinc-800"}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className={cn("flex items-center gap-2 text-base", isLight ? "text-gray-900" : "text-white")}>
                    <ImageIcon className="w-5 h-5" />
                    Galerie d'images
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setAiImagePrompt(`Photo professionnelle de ${formData.name?.fr || 'produit fashion'}, style e-commerce luxe, fond neutre`);
                      setAiImageDialogOpen(true);
                    }}
                    className={cn(
                      "gap-1",
                      isLight ? "border-purple-300 text-purple-600 hover:bg-purple-50" : "border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                    )}
                  >
                    <Wand2 className="w-4 h-4" />
                    Générer IA
                  </Button>
                </div>
                <CardDescription className={isLight ? "text-gray-600" : "text-gray-400"}>
                  Cliquez sur une image pour la mettre en avant ⭐
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-2">
                {/* Featured Image */}
                <div className="aspect-square rounded-lg overflow-hidden bg-zinc-800 mb-4 relative group">
                  <img 
                    src={getImgUrl(featuredImage)}
                    alt={formData.name?.fr}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = '/placeholder.png'; }}
                  />
                  <div className="absolute top-2 right-2">
                    <Badge className="bg-indigo-600 text-white text-xs flex items-center gap-1">
                      <Star className="w-3 h-3 fill-current" />
                      Image mise en avant
                    </Badge>
                  </div>
                </div>
                
                {/* Thumbnails Gallery */}
                {allImages.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className={cn("text-xs", isLight ? "text-gray-500" : "text-gray-400")}>
                        {allImages.length} images disponibles
                      </p>
                      <div className="flex gap-2 text-[10px]">
                        {allImages.filter(i => i.source === 'ai').length > 0 && (
                          <span className="px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">
                            ✨IA: {allImages.filter(i => i.source === 'ai').length}
                          </span>
                        )}
                        {allImages.filter(i => i.source === 'variant').length > 0 && (
                          <span className="px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">
                            Var: {allImages.filter(i => i.source === 'variant').length}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto pr-1">
                      {allImages.map((img, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleSetFeaturedImage(img.url)}
                          className={cn(
                            "aspect-square rounded-md overflow-hidden border-2 transition-all relative group",
                            featuredImage === img.url
                              ? "border-indigo-500 ring-2 ring-indigo-500/50"
                              : isLight
                                ? "border-gray-200 hover:border-indigo-400"
                                : "border-zinc-700 hover:border-indigo-500"
                          )}
                          title={`Source: ${img.source}${img.label ? ` - ${img.label}` : ''}`}
                        >
                          <img 
                            src={getImgUrl(img.url)}
                            alt={`Image ${idx + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          {featuredImage === img.url && (
                            <div className="absolute inset-0 bg-indigo-500/20 flex items-center justify-center">
                              <Star className="w-5 h-5 text-yellow-400 fill-current" />
                            </div>
                          )}
                          {/* Source badge - always visible */}
                          <div className={cn(
                            "absolute top-0.5 left-0.5 text-white text-[8px] px-1 py-0.5 rounded font-medium",
                            img.source === 'ai' ? 'bg-purple-600' : 
                            img.source === 'variant' ? 'bg-blue-600' : 
                            img.source === 'qc' ? 'bg-orange-600' : 'bg-gray-600'
                          )}>
                            {img.source === 'ai' ? '✨' : img.source === 'qc' ? 'QC' : img.source === 'variant' ? 'V' : 'M'}
                          </div>
                          {/* Label for variants */}
                          {img.label && (
                            <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[8px] text-center py-0.5 truncate px-1">
                              {img.label}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Status Info */}
                <div className="space-y-2 mt-4 pt-4 border-t border-zinc-700">
                  <div className="flex items-center justify-between">
                    <span className={cn("text-sm", isLight ? "text-gray-600" : "text-gray-400")}>Statut</span>
                    <Badge className={formData.status === 'published' ? "bg-green-600" : "bg-yellow-600"}>
                      {formData.status === 'published' ? 'Publié' : 'Brouillon'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={cn("text-sm", isLight ? "text-gray-600" : "text-gray-400")}>SKU</span>
                    <code className={cn("text-xs px-2 py-1 rounded", isLight ? "bg-gray-100" : "bg-zinc-800")}>
                      {product?.sku || 'N/A'}
                    </code>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Categories Card */}
            <Card className={isLight ? "bg-white border-gray-200" : "bg-zinc-900/50 border-zinc-800"}>
              <CardHeader>
                <CardTitle className={cn("flex items-center gap-2", isLight ? "text-gray-900" : "text-white")}>
                  <FolderOpen className="w-5 h-5" />
                  Catégories
                </CardTitle>
                <CardDescription className={isLight ? "text-gray-600" : "text-gray-400"}>
                  Assignez ce produit à des catégories
                </CardDescription>
              </CardHeader>
              <CardContent>
                {categories.length === 0 ? (
                  <div className="text-center py-4">
                    <p className={cn("text-sm mb-3", isLight ? "text-gray-500" : "text-gray-500")}>
                      Aucune catégorie disponible
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/admin/categories')}
                      className={isLight ? "border-gray-300" : "border-zinc-700"}
                    >
                      Créer des catégories
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {categories.filter(c => c.is_active).map((category) => {
                      const isSelected = formData.category_ids?.includes(category.id);
                      return (
                        <button
                          key={category.id}
                          onClick={() => toggleCategory(category.id)}
                          className={cn(
                            "w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left",
                            isSelected
                              ? "border-indigo-500 bg-indigo-500/10"
                              : isLight 
                                ? "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                                : "border-zinc-700 hover:border-zinc-600 hover:bg-zinc-800/50"
                          )}
                          data-testid={`category-toggle-${category.slug}`}
                        >
                          <span className="text-xl">{category.emoji || '🏷️'}</span>
                          <div className="flex-1">
                            <p className={cn(
                              "font-medium",
                              isLight ? "text-gray-900" : "text-white"
                            )}>
                              {category.name?.fr || category.slug}
                            </p>
                            <p className={cn("text-xs", isLight ? "text-gray-500" : "text-gray-500")}>
                              {category.products_count || 0} produits
                            </p>
                          </div>
                          {isSelected && (
                            <Check className="w-5 h-5 text-indigo-500" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Prix & Marge Card */}
            <Card className={cn(
              "border-2",
              isLight ? "bg-white border-green-200" : "bg-zinc-900/50 border-green-500/30"
            )}>
              <CardHeader className="pb-3">
                <CardTitle className={cn("flex items-center gap-2 text-sm", isLight ? "text-gray-900" : "text-white")}>
                  <Tag className="w-4 h-4 text-green-500" />
                  Prix & Marge
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Prix source (coût d'achat) */}
                {sourcePrice && (
                  <div className={cn("p-3 rounded-lg border", isLight ? "bg-blue-50 border-blue-200" : "bg-blue-900/20 border-blue-800")}>
                    <p className={cn("text-xs mb-2 font-medium", isLight ? "text-blue-700" : "text-blue-400")}>
                      💰 Prix source (coût d'achat)
                    </p>
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="flex items-baseline gap-1">
                        <span className={cn("text-xl font-bold", isLight ? "text-blue-900" : "text-blue-300")}>
                          ${sourcePrice.price_usd?.toFixed(2) || 'N/A'}
                        </span>
                        <span className={cn("text-xs", isLight ? "text-blue-600" : "text-blue-400")}>USD</span>
                      </div>
                      {sourcePrice.price_cny && (
                        <div className="flex items-baseline gap-1">
                          <span className={cn("text-lg font-semibold", isLight ? "text-gray-700" : "text-gray-300")}>
                            ¥{sourcePrice.price_cny}
                          </span>
                          <span className={cn("text-xs", isLight ? "text-gray-500" : "text-gray-400")}>CNY</span>
                        </div>
                      )}
                      {sourcePrice.price_usd && (
                        <div className="flex items-baseline gap-1">
                          <span className={cn("text-lg font-semibold", isLight ? "text-gray-700" : "text-gray-300")}>
                            €{(sourcePrice.price_usd * 0.92).toFixed(2)}
                          </span>
                          <span className={cn("text-xs", isLight ? "text-gray-500" : "text-gray-400")}>EUR</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {!sourcePrice && (
                  <div className={cn("p-3 rounded-lg border", isLight ? "bg-yellow-50 border-yellow-200" : "bg-yellow-900/20 border-yellow-800")}>
                    <p className={cn("text-xs", isLight ? "text-yellow-700" : "text-yellow-400")}>
                      ⚠️ Prix source non disponible (produit importé manuellement)
                    </p>
                  </div>
                )}
                
                {/* Coefficient multiplicateur */}
                <div className="space-y-2">
                  <Label className={cn("text-xs", isLight ? "text-gray-600" : "text-gray-400")}>
                    Coefficient multiplicateur
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      step="0.1"
                      value={formData.margin_coefficient}
                      onChange={(e) => {
                        const coef = parseFloat(e.target.value) || 1;
                        const newPrice = sourcePrice?.price_usd 
                          ? Math.round(sourcePrice.price_usd * coef * 3.7 * 100) / 100  // USD to ILS
                          : formData.price;
                        setFormData(prev => ({
                          ...prev,
                          margin_coefficient: coef,
                          price: newPrice
                        }));
                      }}
                      className={cn(
                        "w-20 text-center",
                        isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700"
                      )}
                    />
                    <span className={cn("text-sm", isLight ? "text-gray-500" : "text-gray-400")}>
                      × {sourcePrice?.price_usd?.toFixed(2) || '?'} USD
                    </span>
                  </div>
                </div>
                
                {/* Prix de vente */}
                <div className="space-y-2">
                  <Label className={cn("text-xs", isLight ? "text-gray-600" : "text-gray-400")}>
                    Prix de vente (ILS)
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => {
                        const price = parseFloat(e.target.value) || 0;
                        // Recalculer le coefficient si on a le prix source
                        let coef = formData.margin_coefficient;
                        if (sourcePrice?.price_usd) {
                          const priceUSD = price / 3.7;
                          coef = Math.round((priceUSD / sourcePrice.price_usd) * 10) / 10;
                        }
                        setFormData(prev => ({
                          ...prev,
                          price: price,
                          margin_coefficient: coef
                        }));
                      }}
                      className={cn(
                        "flex-1",
                        isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700"
                      )}
                    />
                    <span className={cn("text-lg font-bold", isLight ? "text-gray-900" : "text-white")}>
                      ₪
                    </span>
                  </div>
                </div>
                
                {/* Marge calculée */}
                {sourcePrice?.price_usd && (
                  <div className={cn("p-3 rounded-lg", isLight ? "bg-green-50" : "bg-green-900/20")}>
                    <div className="flex justify-between items-center">
                      <span className={cn("text-xs", isLight ? "text-gray-600" : "text-gray-400")}>
                        Marge estimée
                      </span>
                      <span className={cn("text-sm font-bold", isLight ? "text-green-700" : "text-green-400")}>
                        +{Math.round(((formData.price / 3.7) - sourcePrice.price_usd) * 100) / 100} USD
                        ({Math.round(((formData.price / 3.7 / sourcePrice.price_usd) - 1) * 100)}%)
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* AI Status Card */}
            <Card className={cn(
              "border-2",
              isLight ? "bg-white border-purple-200" : "bg-zinc-900/50 border-purple-500/30"
            )}>
              <CardHeader className="pb-3">
                <CardTitle className={cn("flex items-center gap-2 text-sm", isLight ? "text-gray-900" : "text-white")}>
                  <Sparkles className="w-4 h-4 text-purple-500" />
                  Contenu IA
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className={cn("text-xs", isLight ? "text-gray-600" : "text-gray-400")}>
                  Régénérez automatiquement le titre, la description et les tags dans les 3 langues grâce à l'IA.
                </p>
                <Button
                  onClick={handleRegenerateAI}
                  disabled={regenerating}
                  variant="outline"
                  className={cn(
                    "w-full",
                    isLight 
                      ? "border-purple-300 hover:bg-purple-50 text-purple-700" 
                      : "border-purple-500/50 hover:bg-purple-500/10 text-purple-400"
                  )}
                  data-testid="regenerate-ai-sidebar-btn"
                >
                  {regenerating ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  {regenerating ? 'Génération...' : 'Régénérer'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* AI Preview Dialog */}
      <Dialog open={aiPreviewOpen} onOpenChange={setAiPreviewOpen}>
        <DialogContent className={cn(
          "max-w-5xl max-h-[90vh] overflow-hidden",
          isLight ? "bg-white" : "bg-zinc-900 border-zinc-800"
        )}>
          <DialogHeader>
            <DialogTitle className={cn("flex items-center gap-2", isLight ? "text-gray-900" : "text-white")}>
              <Sparkles className="w-5 h-5 text-purple-500" />
              Prévisualisation du contenu IA
            </DialogTitle>
            <DialogDescription className={isLight ? "text-gray-600" : "text-gray-400"}>
              Comparez l'ancien contenu avec le nouveau généré par l'IA. Validez ou annulez les modifications.
            </DialogDescription>
          </DialogHeader>

          {aiPreviewData && (
            <div className="space-y-4 overflow-y-auto max-h-[60vh] pr-2">
              {/* Language tabs for preview */}
              <Tabs value={previewTab} onValueChange={setPreviewTab} className="w-full">
                <TabsList className={cn(
                  "grid w-full grid-cols-3",
                  isLight ? "bg-gray-100" : "bg-zinc-800"
                )}>
                  <TabsTrigger value="fr">🇫🇷 Français</TabsTrigger>
                  <TabsTrigger value="en">🇬🇧 English</TabsTrigger>
                  <TabsTrigger value="he">🇮🇱 עברית</TabsTrigger>
                </TabsList>

                {['fr', 'en', 'he'].map((lang) => (
                  <TabsContent key={lang} value={lang} className="space-y-4 mt-4">
                    {/* Title comparison */}
                    <div className="space-y-2">
                      <Label className={cn("text-sm font-semibold", isLight ? "text-gray-700" : "text-gray-300")}>
                        Titre
                      </Label>
                      <div className="grid grid-cols-2 gap-4">
                        <div className={cn(
                          "p-3 rounded-lg border",
                          isLight ? "bg-red-50 border-red-200" : "bg-red-950/30 border-red-900/50"
                        )}>
                          <p className={cn("text-xs font-medium mb-1", isLight ? "text-red-600" : "text-red-400")}>
                            ❌ Ancien
                          </p>
                          <p className={cn("text-sm", isLight ? "text-gray-800" : "text-gray-200")} dir={lang === 'he' ? 'rtl' : 'ltr'}>
                            {aiPreviewData.old.name?.[lang] || <span className="italic text-gray-400">Vide</span>}
                          </p>
                        </div>
                        <div className={cn(
                          "p-3 rounded-lg border",
                          isLight ? "bg-green-50 border-green-200" : "bg-green-950/30 border-green-900/50"
                        )}>
                          <p className={cn("text-xs font-medium mb-1", isLight ? "text-green-600" : "text-green-400")}>
                            ✅ Nouveau
                          </p>
                          <p className={cn("text-sm", isLight ? "text-gray-800" : "text-gray-200")} dir={lang === 'he' ? 'rtl' : 'ltr'}>
                            {aiPreviewData.new.name?.[lang] || <span className="italic text-gray-400">Vide</span>}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Description comparison */}
                    <div className="space-y-2">
                      <Label className={cn("text-sm font-semibold", isLight ? "text-gray-700" : "text-gray-300")}>
                        Description
                      </Label>
                      <div className="grid grid-cols-2 gap-4">
                        <div className={cn(
                          "p-3 rounded-lg border max-h-40 overflow-y-auto",
                          isLight ? "bg-red-50 border-red-200" : "bg-red-950/30 border-red-900/50"
                        )}>
                          <p className={cn("text-xs font-medium mb-1", isLight ? "text-red-600" : "text-red-400")}>
                            ❌ Ancien
                          </p>
                          <p className={cn("text-sm whitespace-pre-wrap", isLight ? "text-gray-800" : "text-gray-200")} dir={lang === 'he' ? 'rtl' : 'ltr'}>
                            {aiPreviewData.old.description?.[lang] || <span className="italic text-gray-400">Vide</span>}
                          </p>
                        </div>
                        <div className={cn(
                          "p-3 rounded-lg border max-h-40 overflow-y-auto",
                          isLight ? "bg-green-50 border-green-200" : "bg-green-950/30 border-green-900/50"
                        )}>
                          <p className={cn("text-xs font-medium mb-1", isLight ? "text-green-600" : "text-green-400")}>
                            ✅ Nouveau
                          </p>
                          <p className={cn("text-sm whitespace-pre-wrap", isLight ? "text-gray-800" : "text-gray-200")} dir={lang === 'he' ? 'rtl' : 'ltr'}>
                            {aiPreviewData.new.description?.[lang] || <span className="italic text-gray-400">Vide</span>}
                          </p>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>

              {/* Tags comparison */}
              <div className="space-y-2 pt-2 border-t" style={{ borderColor: isLight ? '#e5e7eb' : '#3f3f46' }}>
                <Label className={cn("text-sm font-semibold", isLight ? "text-gray-700" : "text-gray-300")}>
                  Tags
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className={cn(
                    "p-3 rounded-lg border",
                    isLight ? "bg-red-50 border-red-200" : "bg-red-950/30 border-red-900/50"
                  )}>
                    <p className={cn("text-xs font-medium mb-2", isLight ? "text-red-600" : "text-red-400")}>
                      ❌ Anciens ({aiPreviewData.old.tags?.length || 0})
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {aiPreviewData.old.tags?.length > 0 ? (
                        aiPreviewData.old.tags.map((tag) => (
                          <Badge key={tag} variant="secondary" className={cn(
                            "text-xs",
                            isLight ? "bg-gray-200 text-gray-700" : "bg-zinc-700 text-gray-300"
                          )}>
                            {tag}
                          </Badge>
                        ))
                      ) : (
                        <span className="italic text-gray-400 text-sm">Aucun tag</span>
                      )}
                    </div>
                  </div>
                  <div className={cn(
                    "p-3 rounded-lg border",
                    isLight ? "bg-green-50 border-green-200" : "bg-green-950/30 border-green-900/50"
                  )}>
                    <p className={cn("text-xs font-medium mb-2", isLight ? "text-green-600" : "text-green-400")}>
                      ✅ Nouveaux ({aiPreviewData.new.tags?.length || 0})
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {aiPreviewData.new.tags?.length > 0 ? (
                        aiPreviewData.new.tags.map((tag) => (
                          <Badge key={tag} variant="secondary" className={cn(
                            "text-xs",
                            isLight ? "bg-green-100 text-green-700" : "bg-green-900/50 text-green-300"
                          )}>
                            {tag}
                          </Badge>
                        ))
                      ) : (
                        <span className="italic text-gray-400 text-sm">Aucun tag</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-3 pt-4 border-t" style={{ borderColor: isLight ? '#e5e7eb' : '#3f3f46' }}>
            <Button
              variant="outline"
              onClick={rejectAIChanges}
              className={cn(
                "flex-1",
                isLight ? "border-gray-300 hover:bg-gray-100" : "border-zinc-700 hover:bg-zinc-800"
              )}
              data-testid="reject-ai-changes-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Annuler
            </Button>
            <Button
              onClick={applyAIChanges}
              className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
              data-testid="apply-ai-changes-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Appliquer les modifications
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Image Generation Dialog */}
      <Dialog open={aiImageDialogOpen} onOpenChange={setAiImageDialogOpen}>
        <DialogContent className={cn(
          "max-w-lg",
          isLight ? "bg-white" : "bg-zinc-900 border-zinc-800"
        )}>
          <DialogHeader>
            <DialogTitle className={cn("flex items-center gap-2", isLight ? "text-gray-900" : "text-white")}>
              <Wand2 className="w-5 h-5 text-purple-500" />
              Générer une image par IA
            </DialogTitle>
            <DialogDescription className={isLight ? "text-gray-600" : "text-gray-400"}>
              Décrivez l'image que vous souhaitez créer pour ce produit
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Prompt libre */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className={isLight ? "text-gray-700" : "text-gray-300"}>
                  Prompt libre
                </Label>
                <VoiceInput 
                  onTranscript={(text) => setAiImagePrompt(prev => prev + ' ' + text)}
                  className={isLight ? "border-gray-300" : "border-zinc-700"}
                />
              </div>
              <Textarea
                value={aiImagePrompt}
                onChange={(e) => setAiImagePrompt(e.target.value)}
                placeholder="Ex: Photo studio sur fond blanc, mannequin portant le vêtement, éclairage professionnel... ou utilisez le micro 🎤"
                rows={4}
                className={cn(
                  "w-full",
                  isLight 
                    ? "bg-white border-gray-300 text-gray-900" 
                    : "bg-zinc-800 border-zinc-700 text-white"
                )}
              />
              <p className={cn("text-xs", isLight ? "text-gray-500" : "text-gray-500")}>
                Plus votre description est détaillée, meilleur sera le résultat
              </p>
            </div>

            {/* Quick prompts */}
            <div className="space-y-2">
              <Label className={isLight ? "text-gray-700" : "text-gray-300"}>Suggestions rapides</Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: '📸 Studio', prompt: `Photo studio professionnelle de ${formData.name?.fr || 'ce produit'}, fond blanc immaculé, éclairage doux` },
                  { label: '👤 Mannequin', prompt: `Mannequin portant ${formData.name?.fr || 'le vêtement'}, pose fashion, fond neutre, style lookbook` },
                  { label: '🏙️ Urbain', prompt: `Photo lifestyle urbaine avec ${formData.name?.fr || 'le produit'}, style streetwear, ambiance moderne` },
                  { label: '✨ Luxe', prompt: `Photo haut de gamme de ${formData.name?.fr || 'ce produit'}, ambiance luxueuse, éclairage dramatique` },
                ].map((item) => (
                  <Button
                    key={item.label}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setAiImagePrompt(item.prompt)}
                    className={cn(
                      "text-xs",
                      isLight ? "border-gray-300 hover:bg-gray-100" : "border-zinc-700 hover:bg-zinc-800"
                    )}
                  >
                    {item.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: isLight ? '#e5e7eb' : '#3f3f46' }}>
            <Button
              variant="outline"
              onClick={() => setAiImageDialogOpen(false)}
              className={isLight ? "border-gray-300" : "border-zinc-700"}
            >
              Annuler
            </Button>
            <Button
              onClick={handleGenerateAiImage}
              disabled={generatingAiImage || !aiImagePrompt.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {generatingAiImage ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Wand2 className="w-4 h-4 mr-2" />
              )}
              Générer l'image
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminPageWrapper>
  );
}

export default AdminProductEditPage;
